/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

// Dashboard widget library: top processes and disk (SMART) health for a
// selected agent, plus fleet-wide VPN status and a recent-events log. All four
// read data the backend already collects (agent detail, Tailscale status,
// incident tracker), so they light up on a real deployment with no extra setup.

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { MonitorAgent } from "@/api/monitor";
import { useServerDetail } from "@/hooks/useServerDetail";
import { getIncidents, getServiceUptime } from "@/api/overview";
import { tailscaleAPI } from "@/api/tailscale";
import { formatBytes } from "@/utils/formatBytes";
import { AgentWidgetProps, AgentSlot, NoAgent } from "./ServerWidgets";

// ---- shared helpers --------------------------------------------------------

function cpuBarClass(n: number) {
  return n >= 90 ? "gh-bar--crit" : n >= 60 ? "gh-bar--warn" : "";
}

/** Disk temperature severity. NVMe/SSD run hotter, so the thresholds are a
 *  little higher for non-rotational media. */
function tempClass(temp: number, rotational: boolean) {
  const warn = rotational ? 48 : 60;
  const crit = rotational ? 55 : 70;
  return temp >= crit ? "gh-crit" : temp >= warn ? "gh-warn" : "";
}

function shortModel(model?: string) {
  if (!model) return "";
  return model.replace(/\s+/g, " ").trim().slice(0, 24);
}

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)}m`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)}h`;
  return `${Math.floor(h / 24)}d`;
}

// ---- Top processes (agent-scoped) -----------------------------------------

export const ProcessesWidget: React.FC<AgentWidgetProps> = ({ agent, agents, onAgentChange }) => {
  if (!agent) return <NoAgent label="Top processes" />;
  return <ProcessesInner agent={agent} agents={agents} onAgentChange={onAgentChange} />;
};

const ProcessesInner: React.FC<{
  agent: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}> = ({ agent, agents, onAgentChange }) => {
  const { detail, isError } = useServerDetail(agent);
  const procs = [...(detail?.processes ?? [])].sort((a, b) => b.cpu_percent - a.cpu_percent).slice(0, 8);

  return (
    <section className={`gh-w${procs.length === 0 ? " gh-w--compact" : ""}`}>
      <div className="gh-w-head">
        <span className="gh-label">Top processes</span>
        <AgentSlot agent={agent} agents={agents} onAgentChange={onAgentChange} />
      </div>
      {procs.length === 0 ? (
        <div className="gh-empty">{isError ? "agent offline" : "no process data yet"}</div>
      ) : (
        <div className="gh-proc">
          {procs.map((p) => (
            <div className="gh-proc-row" key={p.pid}>
              <div className="gh-proc-k" title={`${p.name} · pid ${p.pid}${p.user ? ` · ${p.user}` : ""}`}>
                {p.name}
                <small>
                  {p.user ? `${p.user} · ` : ""}
                  {formatBytes(p.mem_bytes)}
                </small>
              </div>
              <div className="gh-bar">
                <i className={cpuBarClass(p.cpu_percent)} style={{ width: `${Math.min(100, p.cpu_percent)}%` }} />
              </div>
              <div className="gh-res-v">{(+p.cpu_percent).toFixed(0)}%</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ---- Disk / SMART health (agent-scoped) -----------------------------------

export const DiskHealthWidget: React.FC<AgentWidgetProps> = ({ agent, agents, onAgentChange }) => {
  if (!agent) return <NoAgent label="Disk health" />;
  return <DiskHealthInner agent={agent} agents={agents} onAgentChange={onAgentChange} />;
};

const DiskHealthInner: React.FC<{
  agent: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}> = ({ agent, agents, onAgentChange }) => {
  const { detail, isError } = useServerDetail(agent);
  const disks = detail?.smart ?? [];

  return (
    <section className={`gh-w${disks.length === 0 ? " gh-w--compact" : ""}`}>
      <div className="gh-w-head">
        <span className="gh-label">Disk health</span>
        <AgentSlot agent={agent} agents={agents} onAgentChange={onAgentChange} />
      </div>
      {disks.length === 0 ? (
        <div className="gh-empty">{isError ? "agent offline" : "no SMART data — needs smartctl on the agent"}</div>
      ) : (
        <div className="gh-disk">
          {disks.map((d) => {
            const failed = d.health === "failed";
            const dotCls = failed ? "gh-dot--down" : d.health === "passed" ? "gh-dot--up" : "gh-dot--warn";
            return (
              <div className="gh-disk-row" key={d.device}>
                <span className={`gh-prob-dot ${dotCls}`} />
                <div className="gh-disk-k" title={`${d.device}${d.model ? ` · ${d.model}` : ""}`}>
                  {shortModel(d.model) || d.device}
                  <small>
                    {d.rotational ? "HDD" : d.protocol === "NVMe" ? "NVMe" : "SSD"}
                    {d.capacity_bytes ? ` · ${formatBytes(d.capacity_bytes)}` : ""}
                    {d.wear_percent_used != null && !d.rotational ? ` · ${d.wear_percent_used}% wear` : ""}
                  </small>
                </div>
                <div className={`gh-disk-temp ${d.temp_c != null ? tempClass(d.temp_c, d.rotational) : ""}`}>
                  {d.temp_c != null ? `${Math.round(d.temp_c)}°` : d.health === "standby" ? "idle" : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ---- VPN / Tailscale status (fleet-wide) ----------------------------------

export const VpnWidget: React.FC = () => {
  const { data, isError } = useQuery({
    queryKey: ["tailscale-status"],
    queryFn: () => tailscaleAPI.getDiscoveryStatus(),
    refetchInterval: 30000,
    retry: false,
  });

  const online = data?.online ?? false;
  const dotCls = !data?.enabled ? "gh-dot--warn" : online ? "gh-dot--up" : "gh-dot--down";

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">VPN · Tailscale</span>
        <span className="gh-sub">{data?.enabled ? (online ? "online" : "offline") : "off"}</span>
      </div>
      {isError || !data ? (
        <div className="gh-empty">{isError ? "status unavailable" : "loading…"}</div>
      ) : !data.enabled ? (
        <div className="gh-empty">Tailscale is disabled</div>
      ) : (
        <div className="gh-vpn">
          <div className="gh-vpn-head">
            <span className={`gh-prob-dot ${dotCls}`} />
            <span className="gh-vpn-host">{data.hostname || "this node"}</span>
          </div>
          <div className="gh-vpn-meta">
            {data.tailscale_ips?.[0] && <span className="gh-vpn-ip">{data.tailscale_ips[0]}</span>}
            {data.magic_dns_suffix && <span>{data.magic_dns_suffix}</span>}
          </div>
          {data.discovered_agents != null && (
            <div className="gh-vpn-peers">
              <b>{data.discovered_agents}</b> discovered {data.discovered_agents === 1 ? "agent" : "agents"}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// ---- Service uptime (fleet-wide) ------------------------------------------

function uptimeBarClass(pct: number) {
  return pct < 95 ? "gh-bar--crit" : pct < 99.5 ? "gh-bar--warn" : "";
}
function uptimeTextClass(pct: number) {
  return pct < 95 ? "gh-crit" : pct < 99.5 ? "gh-warn" : "";
}

export const ServiceUptimeWidget: React.FC = () => {
  const { data: services = [], isError } = useQuery({
    queryKey: ["service-uptime"],
    queryFn: getServiceUptime,
    refetchInterval: 60000,
    retry: false,
  });

  // Only services that are not perfect need a row; the rest collapse to dots.
  const attention = services.filter((s) => s.uptime7d < 100 || s.current !== "up");
  const perfect = services.filter((s) => s.uptime7d >= 100 && s.current === "up");

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">Service uptime</span>
        <span className="gh-sub">7-day</span>
      </div>
      {services.length === 0 ? (
        <div className="gh-empty">{isError ? "unavailable" : "no services yet"}</div>
      ) : (
        <>
          {attention.length > 0 && (
            <div className="gh-up">
              {attention.map((svc) => {
                const dotCls =
                  svc.current === "down" ? "gh-dot--down" : svc.current === "up" ? "gh-dot--up" : "gh-dot--warn";
                return (
                  <div className="gh-up-row" key={svc.name}>
                    <span className={`gh-prob-dot ${dotCls}`} />
                    <div className="gh-up-k" title={`${svc.name} · 24h ${svc.uptime24h}% · 30d ${svc.uptime30d}%`}>
                      {svc.name}
                      <small>{`24h ${svc.uptime24h}% · 30d ${svc.uptime30d}%`}</small>
                    </div>
                    <div className="gh-bar">
                      <i className={uptimeBarClass(svc.uptime7d)} style={{ width: `${svc.uptime7d}%` }} />
                    </div>
                    <div className={`gh-res-v ${uptimeTextClass(svc.uptime7d)}`}>{svc.uptime7d}%</div>
                  </div>
                );
              })}
            </div>
          )}
          {perfect.length > 0 && (
            <div className="cc-up-ok">
              <div className="gh-sub">{`${perfect.length} ${attention.length ? "others" : "services"} at 100%`}</div>
              <div className="cc-up-dots">
                {perfect.map((svc) => (
                  <span key={svc.name} className="cc-up-dot" title={`${svc.name} · 100%`} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

// ---- Recent events / incident log (fleet-wide) ----------------------------

export const EventLogWidget: React.FC = () => {
  const { data: incidents = [], isError } = useQuery({
    queryKey: ["incidents"],
    queryFn: getIncidents,
    refetchInterval: 30000,
    retry: false,
  });

  // Open incidents already live in "Open problems"; this is the history. Runs of
  // the same incident (a service that keeps flapping) collapse into one row.
  const resolved = incidents
    .filter((i) => i.resolvedAt)
    .sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt));
  const events: { key: string; name: string; reason: string; at: string; count: number; crit: boolean }[] = [];
  for (const e of resolved) {
    const last = events[events.length - 1];
    if (last && last.name === e.name && last.reason === e.reason) {
      last.count++;
    } else {
      events.push({ key: String(e.id), name: e.name, reason: e.reason, at: e.resolvedAt!, count: 1, crit: e.severity === "crit" });
    }
  }
  const shown = events.slice(0, 8);

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">Recent events</span>
        <span className="gh-sub">{`${resolved.length} resolved`}</span>
      </div>
      {shown.length === 0 ? (
        <div className="gh-empty">{isError ? "unavailable" : "nothing resolved yet"}</div>
      ) : (
        <div className="gh-ev">
          {shown.map((e) => (
            <div className="gh-ev-row" key={e.key}>
              <span className={`gh-prob-dot ${e.crit ? "gh-dot--down" : "gh-dot--warn"}`} />
              <div className="gh-ev-k" title={`${e.name} — ${e.reason}`}>
                <span className="gh-ev-name">
                  {e.name}
                  {e.count > 1 && <em className="cc-ev-count">{`×${e.count}`}</em>}
                </span>
                <small>{e.reason}</small>
              </div>
              <div className="gh-ev-time">{`${timeAgo(e.at)} ago`}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
